"use client";

import LocaleLink from "@/component/layout/LocaleLink";
import { useArticleCategories } from "@/hooks/useArticles";
import type { Article, Locale } from "@/types/insights";
import { motion } from "framer-motion";
import { MoveLeft } from "lucide-react";
import { useLocale } from "next-intl";

/** The featured (lead) article block. */
export default function FeaturedArticle({ article }: { article: Article }) {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const { data: cats = [] } = useArticleCategories();
  const cat = cats.find((c) => c.id === article.category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="group grid gap-8 lg:grid-cols-2 lg:items-center"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={article.image} alt={article.title[locale]} className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0" />
      </div>
      <div>
        <div className="flex items-center gap-4 text-[11px] uppercase tracking-widest">
          <span className="text-accent">{cat?.label[locale]}</span>
          <span className="text-ash/50 dark:text-white/50">{article.date[locale]}</span>
        </div>
        <h2 className="mt-4 font-bebas text-[clamp(2rem,4vw,3.4rem)] uppercase leading-tight text-ash dark:text-white">
          {article.title[locale]}
        </h2>
        <p className="mt-4 leading-relaxed text-ash/60 dark:text-white/60">{article.excerpt[locale]}</p>
        <LocaleLink href={`/insights/${article.id}`} className="group/l mt-6 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-widest text-ash dark:text-white">
          <span className="border-b border-transparent pb-0.5 transition-colors group-hover/l:border-accent">{ar ? "اقرأ المقال" : "Read article"}</span>
          <MoveLeft className="h-4 w-4 text-accent rtl:rotate-180" />
        </LocaleLink>
      </div>
    </motion.div>
  );
}
